import { removeLocalStorage, setLocalStorage } from "@/hooks/useLocalStorage";
import { getCookie, setCookie } from "./cookie";
import customAlert from "./customAlert";

const PUBLIC_VAPID_KEY =
  "BMI4fBPvbqACYkqdbuBT90oyAbtEOHbZNx_g4Qdi5fEO7Kn-dwpd-4feSyyheBRc76nbHilmO6VJFm39f8qEF50";

const REGISTER_COOKIE_NAME = "REGISTER_SW_EXPIRED";
const PUSH_ENDPOINT_COOKIE_NAME = "PUSH_ENDPOINT";

// const REGISTER_COOLDOWN = 60 * 1000; // 1분
// const REGISTER_COOLDOWN = 60 * 60 * 1000; // 1시간
const REGISTER_COOLDOWN = 24 * 60 * 60 * 1000; // 24시간
// const REGISTER_COOLDOWN_Y = 24 * 60 * 60 * 1000 * 365; // 1년

type NavigatorWithStandalone = Navigator & {
  standalone?: boolean;
};

/**
 * Base64 문자열을 Push API에서 사용할 Uint8Array로 변환
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat(
    (4 - (base64String.length % 4)) % 4,
  );

  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

/**
 * 설치된 PWA 또는 홈 화면 앱 실행 여부
 */
function isStandaloneMode(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const displayModeStandalone = window.matchMedia(
    "(display-mode: standalone)",
  ).matches;

  const iosStandalone =
    (window.navigator as NavigatorWithStandalone).standalone ===
    true;

  return displayModeStandalone || iosStandalone;
}

/**
 * iOS 계열 브라우저 여부
 */
function isIOS(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }

  const userAgent = navigator.userAgent;

  return (
    /iPhone|iPad|iPod/i.test(userAgent) ||
    (navigator.platform === "MacIntel" &&
      navigator.maxTouchPoints > 1)
  );
}

/**
 * 푸시 기능 지원 여부
 */
function isPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/**
 * Service Worker 등록
 */
async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  const registration =
    await navigator.serviceWorker.register("/sw.js", {
      scope: "/",
    });

  console.log("✅ Service Worker 등록 완료", {
    scope: registration.scope,
    active: registration.active?.scriptURL,
    installing: registration.installing?.scriptURL,
    waiting: registration.waiting?.scriptURL,
  });

  return registration;
}

/**
 * 서버 구독 등록 API 주소
 */
function getSubscribeApiUrl(): string {
  const path = "/api/push/subscribe/";

  if (process.env.NODE_ENV === "development") {
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE?.replace(/\/$/, "") ?? "";

    return `${baseUrl}${path}`;
  }

  return path;
}

/**
 * PushSubscription을 서버에 저장
 */
async function saveSubscription(
  subscription: PushSubscription,
): Promise<string> {
  const input = getSubscribeApiUrl();

  console.log("📡 푸시 구독 서버 전송 시작", {
    input,
    endpoint: subscription.endpoint,
  });
  const code = getCookie("code") ?? "";

  const subscriptionData = subscription.toJSON();

  const response = await fetch(input, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ ...subscriptionData, code }),
  });

  let responseData: unknown = null;

  try {
    responseData = await response.json();
  } catch {
    responseData = null;
  }

  console.log("📡 푸시 구독 서버 응답", {
    status: response.status,
    ok: response.ok,
    data: responseData,
  });

  if (!response.ok) {
    deleteEndpoint();
    throw new Error(
      `푸시 서버 등록 실패 (${response.status})`,
    );
  }

  setLocalStorage(PUSH_ENDPOINT_COOKIE_NAME, subscription.endpoint);
  return "푸시 구독 서버 동기화 완료";
}

/**
 * 구독 엔드포인트 제거
 */
function deleteEndpoint() {
  removeLocalStorage(PUSH_ENDPOINT_COOKIE_NAME)
}

/**
 * 기존 구독 확인
 */
async function getExistingSubscription(
  registration: ServiceWorkerRegistration,
): Promise<PushSubscription | null> {
  const subscription =
    await registration.pushManager.getSubscription();

  console.log("🔍 기존 푸시 구독 확인", {
    exists: Boolean(subscription),
    endpoint: subscription?.endpoint ?? null,
  });

  return subscription;
}

/**
 * 신규 PushSubscription 생성
 *
 * 이 함수는 사용자 클릭 이벤트 흐름 안에서 호출하는 것이 안전함
 */
async function createPushSubscription(
  registration: any,
): Promise<PushSubscription> {
  console.log("📡 신규 푸시 구독 생성 시작");

  const subscription =
    await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey:
        urlBase64ToUint8Array(PUBLIC_VAPID_KEY),
    });

  console.log("✅ 신규 푸시 구독 생성 완료", {
    endpoint: subscription.endpoint,
  });

  return subscription;
}

/**
 * 등록 시도 쿨타임 쿠키 설정
 */
function setRegisterCooldown(): void {
  const expires = new Date(
    Date.now() + REGISTER_COOLDOWN,
  );

  setCookie(REGISTER_COOKIE_NAME, "1", {
    path: "/",
    expires,
  });
}

/**
 * 사용자 클릭으로 실행할 실제 푸시 등록 함수
 *
 * customAlert 콜백 또는 버튼 onClick에서 직접 실행
 */
export async function registPushAlert(): Promise<string> {
  console.log("🔔 사용자 동작 기반 푸시 등록 시작");

  // 엔드포인트 저장 이벤트 감지 추가
  window.dispatchEvent(new Event("push-subscription-changed"));

  if (typeof window === "undefined") {
    return "window undefined";
  }

  if (!isPushSupported()) {
    const message =
      "❌ 푸시를 지원하지 않는 브라우저입니다.";

    deleteEndpoint();
    console.warn(message);
    return message;
  }

  try {
    const standalone = isStandaloneMode();
    const ios = isIOS();

    console.log("🔍 푸시 등록 환경", {
      standalone,
      ios,
      permission: Notification.permission,
      userAgent: navigator.userAgent,
    });

    /*
     * iOS Web Push는 홈 화면에 설치된 웹앱에서 사용하는 것이 안전함
     */
    if (ios && !standalone) {
      const message =
        "iOS에서는 홈 화면에 앱을 설치한 후 푸시 알림을 등록해주세요.";

      console.warn(message);
      customAlert(message, "warning");

      return message;
    }

    let permission = Notification.permission;

    /*
     * 반드시 customAlert 확인 버튼이나 실제 버튼 클릭 흐름에서 실행
     */
    if (permission === "default") {
      permission =
        await Notification.requestPermission();

      console.log("🔔 알림 권한 요청 결과", {
        permission,
      });
    }

    if (permission !== "granted") {
      const message = a("notificationPermissionDenied");

      console.warn(message, {
        permission,
      });

      customAlert(message, "warning");
      setRegisterCooldown();

      return `${message} (${permission})`;
    }

    const registration =
      await registerServiceWorker();

    let subscription =
      await getExistingSubscription(registration);

    if (!subscription) {
      subscription =
        await createPushSubscription(registration);
    }

    const syncMessage =
      await saveSubscription(subscription);

    setRegisterCooldown();

    const message = a("pushRegistered");

    customAlert(message, "success");

    return `${message} / ${syncMessage}`;
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : String(error);

    console.error("❌ 푸시 등록 중 오류", error);

    /*
     * 일시적인 오류일 수도 있으므로
     * PUSH_ENDPOINT 쿠키를 무조건 삭제하지 않음
     */
    setRegisterCooldown();

    const message = a("pushRegistrationError");

    customAlert(
      `${message}\n${errorMessage}`,
      "error",
    );

    return `${message}: ${errorMessage}`;
  }
}

/**
 * 앱 실행 시 자동 호출할 메인 함수
 *
 * - Service Worker 등록
 * - 기존 구독 확인
 * - 기존 구독이 있다면 서버 동기화
 * - 권한 요청이 필요하면 사용자 클릭용 안내 표시
 *
 * 신규 권한 요청과 신규 subscribe는 registPushAlert()에서 처리
 */
const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

async function executeRegisterPush(): Promise<string> {
  const cooldownCookie = getCookie(REGISTER_COOKIE_NAME);

  console.log("🔍 푸시 자동 확인 초기 상태", {
    cooldownCookie,
    permission: Notification.permission,
    standalone: isStandaloneMode(),
    ios: isIOS(),
  });

  const registration = await registerServiceWorker();
  const existing = await getExistingSubscription(registration);

  /*
   * 기존 구독이 존재하면 서버에 다시 동기화
   */
  if (existing) {
    const syncMessage = await saveSubscription(existing);

    setRegisterCooldown();

    return `✅ 이미 푸시 구독된 상태입니다. / ${syncMessage}`;
  }

  /*
   * 기존 구독이 없고 권한이 차단된 상태
   */
  if (Notification.permission === "denied") {
    const message =
      "❌ 알림 권한이 차단되어 있습니다. 브라우저 사이트 설정에서 알림 권한을 허용해주세요.";

    console.warn(message);
    setRegisterCooldown();

    return message;
  }

  /*
   * 권한은 허용됐지만 구독이 없는 상태
   */
  if (Notification.permission === "granted") {
    const message = a("pushSubscriptionMissing");

    console.warn(message);

    customAlert(message, "warning", () => {
      void registPushAlert();
    });

    setRegisterCooldown();
    deleteEndpoint();
    return message;
  }

  /*
   * permission === "default"
   */
  if (cooldownCookie) {
    return "푸시 등록 안내를 이미 시도했습니다. (쿨타임)";
  }

  const ios = isIOS();
  const standalone = isStandaloneMode();

  if (ios && !standalone) {
    const message = a("iosPushRequiresInstallation");

    customAlert(message, "warning");
    setRegisterCooldown();

    return message;
  }

  const message = a("notificationPermissionRequired");

  customAlert(message, "warning", () => {
    void registPushAlert();
  });
  deleteEndpoint();
  setRegisterCooldown();

  return "알림 권한 등록 대기 중";
}

export async function registerPush(): Promise<string> {
  console.log("🚀 자동 푸시 상태 확인 시작");

  if (typeof window === "undefined") {
    return "window undefined";
  }

  if (!isPushSupported()) {
    const message =
      "❌ 푸시를 지원하지 않는 브라우저입니다.";

    console.warn(message);
    deleteEndpoint();
    return message;
  }

  const maxAttempts = 2;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      console.log(`📡 푸시 상태 확인 시도 ${attempt}/${maxAttempts}`);

      const result = await executeRegisterPush();

      if (attempt > 1) {
        console.log("✅ 푸시 상태 확인 재시도 성공");
      }

      return result;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : String(error);

      console.error(
        `❌ 푸시 상태 확인 실패 ${attempt}/${maxAttempts}`,
        error,
      );
      deleteEndpoint();
      const isLastAttempt = attempt === maxAttempts;

      if (isLastAttempt) {
        return `푸시 상태 확인 실패: ${errorMessage}`;
      }

      console.warn("🔄 푸시 상태 확인을 1회 재시도합니다.");

      await sleep(5000);
    }
  }

  return "푸시 상태 확인 실패";
}

function a(key: string): string {
  const language = String(getCookie("lang") ?? "ko");

  const transMessages: Record<
    string,
    Record<string, string>
  > = {
    ko: {
      pushRegistered:
        "푸시 등록이 완료되었습니다.",
      pushRegistrationFailed:
        "푸시 등록에 실패했습니다. 권한을 다시 확인해주세요.",
      pushRegistrationError:
        "푸시 등록 중 오류가 발생했습니다.",
      notificationPermissionDenied:
        "알림 권한이 허용되지 않았습니다.",
      pushRegistrationFailedSimple:
        "푸시 등록에 실패했습니다.",

      iosInstallRequired:
        "iOS에서는 홈 화면에 앱을 설치한 후 푸시 알림을 등록해주세요.",
      pushSubscriptionMissing:
        "알림 권한은 허용되어 있지만 푸시 구독이 없습니다. 등록 버튼을 눌러주세요.",
      iosPushRequiresInstallation:
        "iOS에서는 홈 화면에 앱을 설치한 후 푸시 알림을 등록할 수 있습니다.",
      notificationPermissionRequired:
        "푸시 알림을 받으려면 알림 권한 등록이 필요합니다.",
    },

    en: {
      pushRegistered:
        "Push registration completed.",
      pushRegistrationFailed:
        "Push registration failed. Please check your permissions.",
      pushRegistrationError:
        "An error occurred during push registration.",
      notificationPermissionDenied:
        "Notification permission is not granted.",
      pushRegistrationFailedSimple:
        "Push registration failed.",

      iosInstallRequired:
        "On iOS, install the app on your Home Screen before enabling push notifications.",
      pushSubscriptionMissing:
        "Notification permission is granted, but no push subscription exists. Please press the registration button.",
      iosPushRequiresInstallation:
        "On iOS, push notifications can be enabled after installing the app on your Home Screen.",
      notificationPermissionRequired:
        "Notification permission is required to receive push notifications.",
    },

    zh: {
      pushRegistered:
        "推送通知注册已完成。",
      pushRegistrationFailed:
        "推送通知注册失败，请重新检查通知权限。",
      pushRegistrationError:
        "推送通知注册过程中发生错误。",
      notificationPermissionDenied:
        "通知权限未被允许。",
      pushRegistrationFailedSimple:
        "推送通知注册失败。",

      iosInstallRequired:
        "在 iOS 上，请先将应用添加到主屏幕，然后再注册推送通知。",
      pushSubscriptionMissing:
        "通知权限已允许，但尚未创建推送订阅。请点击注册按钮。",
      iosPushRequiresInstallation:
        "在 iOS 上，将应用添加到主屏幕后即可注册推送通知。",
      notificationPermissionRequired:
        "接收推送通知需要允许通知权限。",
    },

    id: {
      pushRegistered:
        "Pendaftaran notifikasi push berhasil.",
      pushRegistrationFailed:
        "Pendaftaran notifikasi push gagal. Silakan periksa kembali izin notifikasi.",
      pushRegistrationError:
        "Terjadi kesalahan saat mendaftarkan notifikasi push.",
      notificationPermissionDenied:
        "Izin notifikasi belum diberikan.",
      pushRegistrationFailedSimple:
        "Pendaftaran notifikasi push gagal.",

      iosInstallRequired:
        "Di iOS, instal aplikasi ke Layar Utama terlebih dahulu sebelum mendaftarkan notifikasi push.",
      pushSubscriptionMissing:
        "Izin notifikasi telah diberikan, tetapi langganan push belum tersedia. Silakan tekan tombol daftar.",
      iosPushRequiresInstallation:
        "Di iOS, notifikasi push dapat didaftarkan setelah aplikasi diinstal ke Layar Utama.",
      notificationPermissionRequired:
        "Izin notifikasi diperlukan untuk menerima notifikasi push.",
    },

    ms: {
      pushRegistered:
        "Pendaftaran notifikasi push berjaya.",
      pushRegistrationFailed:
        "Pendaftaran notifikasi push gagal. Sila semak semula kebenaran notifikasi.",
      pushRegistrationError:
        "Ralat berlaku semasa mendaftarkan notifikasi push.",
      notificationPermissionDenied:
        "Kebenaran notifikasi belum diberikan.",
      pushRegistrationFailedSimple:
        "Pendaftaran notifikasi push gagal.",

      iosInstallRequired:
        "Pada iOS, pasang aplikasi pada Skrin Utama terlebih dahulu sebelum mendaftarkan notifikasi push.",
      pushSubscriptionMissing:
        "Kebenaran notifikasi telah diberikan, tetapi langganan push belum tersedia. Sila tekan butang daftar.",
      iosPushRequiresInstallation:
        "Pada iOS, notifikasi push boleh didaftarkan selepas aplikasi dipasang pada Skrin Utama.",
      notificationPermissionRequired:
        "Kebenaran notifikasi diperlukan untuk menerima notifikasi push.",
    },
    
    th: {
      pushRegistered:
        "ลงทะเบียนการแจ้งเตือนแบบพุชเรียบร้อยแล้ว",
      pushRegistrationFailed:
        "ลงทะเบียนการแจ้งเตือนแบบพุชไม่สำเร็จ กรุณาตรวจสอบสิทธิ์อีกครั้ง",
      pushRegistrationError:
        "เกิดข้อผิดพลาดระหว่างการลงทะเบียนการแจ้งเตือนแบบพุช",
      notificationPermissionDenied:
        "ไม่ได้รับอนุญาตให้ส่งการแจ้งเตือน",
      pushRegistrationFailedSimple:
        "ลงทะเบียนการแจ้งเตือนแบบพุชไม่สำเร็จ",

      iosInstallRequired:
        "บน iOS กรุณาติดตั้งแอปไว้ที่หน้าจอโฮมก่อน แล้วจึงลงทะเบียนการแจ้งเตือนแบบพุช",
      pushSubscriptionMissing:
        "อนุญาตการแจ้งเตือนแล้ว แต่ยังไม่มีการสมัครรับการแจ้งเตือนแบบพุช กรุณากดปุ่มลงทะเบียน",
      iosPushRequiresInstallation:
        "บน iOS สามารถลงทะเบียนการแจ้งเตือนแบบพุชได้หลังจากติดตั้งแอปไว้ที่หน้าจอโฮมแล้ว",
      notificationPermissionRequired:
        "ต้องอนุญาตการแจ้งเตือนเพื่อรับการแจ้งเตือนแบบพุช",
    },

    "es-419": {
      pushRegistered:
        "El registro de notificaciones push se completó correctamente.",
      pushRegistrationFailed:
        "No se pudo registrar las notificaciones push. Verifica los permisos nuevamente.",
      pushRegistrationError:
        "Ocurrió un error durante el registro de las notificaciones push.",
      notificationPermissionDenied:
        "No se concedió el permiso para las notificaciones.",
      pushRegistrationFailedSimple:
        "No se pudo registrar las notificaciones push.",

      iosInstallRequired:
        "En iOS, instala la app en la pantalla de inicio antes de registrar las notificaciones push.",
      pushSubscriptionMissing:
        "El permiso de notificaciones está habilitado, pero no hay una suscripción push. Presiona el botón de registro.",
      iosPushRequiresInstallation:
        "En iOS, puedes registrar las notificaciones push después de instalar la app en la pantalla de inicio.",
      notificationPermissionRequired:
        "Debes habilitar el permiso de notificaciones para recibir notificaciones push.",
    },
  };

  return (
    transMessages[language]?.[key] ??
    transMessages.ko[key] ??
    ""
  );
}